import type { IFeatureFlags } from '../../types';

export const DefaultFlow = 'complete';

export const NaNCurrencyCode = 'NaN';

export const NULL = 'NULL';

export const Asterisk = '*';

export const DefaultLanguage = {
  Id: 'en-US',
  Value: [{ code: 'en-US', name: 'English' }],
};

export const ModelInclusion = {
  Id: 'MODEL_INCLUSION',
  Included: 'INCLUDED',
};

export const ApplicationSupportedLanguages = [
  {
    name: 'English',
    code: 'en-US',
  },
  {
    name: 'German',
    code: 'de-DE',
  },
  {
    name: 'French',
    code: 'fr-FR',
  },
  {
    name: 'Italian',
    code: 'it-IT',
  },
  {
    name: 'Japanese',
    code: 'ja-JP',
  },
];

export const ProductScope = {
  BuildDateId: 'DIM_BUILDDATE',
  EventId: 'EVENT',
  MarketId: 'MRKT',
};

export const UserAssignmentsScope = [
  ProductScope.BuildDateId,
  ModelInclusion.Id,
  ProductScope.MarketId,
];

export const ProductSection = {
  HiddenSection: '_HIDDEN_',
};

export const IdFormat = {
  Suffix: {
    SubmodelId: '_C',
    Section: 'S',
    LessFeature: '_LESS',
    Role: 'ROLE',
  },
  Regex: {
    ScopeSection: /Scope\d+$/i,
    Section: /_\d+$/,
  },
  CodeSeparator: '.',
};

export const PriorityValue = {
  High: 999,
  Medium: 998,
  Normal: 997,
};

export const ScopeSectionVariableIds: string[] = [
  ProductScope.BuildDateId,
  ProductScope.EventId,
  ProductScope.MarketId,
];

//array of variableIds whose name is changed
export const ChangedNameVariableIds: string[] = [ProductScope.BuildDateId, ProductScope.MarketId];

export const PopperModifier = [
  {
    name: 'flip',
    enabled: false,
    options: {
      altBoundary: false,
      rootBoundary: 'viewport',
    },
  },
  {
    name: 'preventOverflow',
    enabled: true,
    options: {
      altAxis: true,
      altBoundary: true,
      tether: true,
      rootBoundary: 'document',
    },
  },
];

export const DefaultUserSettings = {
  language: DefaultLanguage.Id,
  showCode: true,
};

export const FeatureFlags: IFeatureFlags = {
  Currency: {
    name: 'Currency',
    active: false,
    visible: false,
    editable: false,
  },
  Language: {
    name: 'Language',
    active: false,
    visible: false,
    editable: false,
  },
  Optional: {
    name: 'Optional',
    active: false,
    visible: false,
    editable: false,
  },
  ListPrice: {
    name: 'ListPrice',
    active: false,
    visible: false,
    editable: false,
  },
  ExportConfiguration: {
    name: 'ExportConfiguration',
    active: false,
    visible: false,
    editable: false,
  },
  DetailedConfiguration: {
    name: 'DetailedConfiguration',
    active: false,
    visible: false,
    editable: false,
  },
  PriceDisclaimer: {
    name: 'PriceDisclaimer',
    active: false,
    visible: false,
    editable: false,
  },
  SimpleConfigurationSummary: {
    name: 'SimpleConfigurationSummary',
    active: false,
    visible: false,
    editable: false,
  },
  ECForSAP: {
    name: 'ECForSAP',
    active: false,
    visible: false,
    editable: false,
  },
  ASItemsDialog: {
    name: 'ASItemsDialog',
    active: false,
    visible: false,
    editable: false,
  },
  DisplayConfigurationState: {
    name: 'DisplayConfigurationState',
    active: false,
    visible: false,
    editable: false,
  },
  AllowPartialConfiguration: {
    name: 'AllowPartialConfiguration',
    active: false,
    visible: false,
    editable: false,
  },
  CustomerPricing: {
    name: 'CustomerPricing',
    active: false,
    visible: false,
    editable: false,
  },
};

export const RecordsPerPage = [15, 25, 50, 100];

export enum EConfigitProperty {
  ReadOnly = 'REO',
  Hide = 'HDE',
  SubModelQty = 'SMQ',
  BundleContent = 'BCT',
  IsOptional = 'OPT',
  ProcosMarketAvailable = 'PMA',
  RulesMarketAvailable = 'RMA',
  IsPasswordProtected = 'IPP',
  IsPassword = 'ISP',
  IsBundleDriving = 'IBD',
}

export enum EPDMProperty {
  OrderingInstructions = 'ORI',
  MaxLength = 'MLE',
  IsServiceRelevant = 'SRI',
  Guarded = 'GRD',
  IsPriceRelevant = 'IPR',
}

//Below properties should use PDM property values instead of Configit
export const PdmProperties = [
  { name: 'maxLength', code: EPDMProperty.MaxLength },
  { name: 'serviceRelevant', code: EPDMProperty.IsServiceRelevant },
  { name: 'orderingInstructions', code: EPDMProperty.OrderingInstructions },
  { name: 'priceRelevant', code: EPDMProperty.IsPriceRelevant },
  { name: 'guarded', code: EPDMProperty.Guarded },
];

export const ConfigitProperties = [
  EConfigitProperty.ReadOnly,
  EConfigitProperty.Hide,
  EConfigitProperty.IsOptional,
  EConfigitProperty.ProcosMarketAvailable,
  EConfigitProperty.RulesMarketAvailable,
];

export enum EConfigurationType {
  Active = 'Active',
  ReadOnly = 'ReadOnly',
  Temp = 'Temp',
  UnAvailable = 'Unavailable',
  Frozen = 'Frozen',
}

export enum EConfigurationStateCode {
  UnAvailable = '3',
  Temp = '2',
  Frozen = '4',
}

export const RestrictedStates = [
  EConfigurationType.ReadOnly,
  EConfigurationType.UnAvailable,
  EConfigurationType.Frozen,
];

export const EJustification = {
  Phase: 'Phase',
  Default: 'ByDefault',
  None: 'None',
  Assignment: 'Assignment',
  Rule: 'ByRule',
  User: 'ByUser',
  // ByRange is a custom origin (not a Configit value): assigned when we fall back to the numeric range minimum for an optional variable
  Range: 'ByRange',
} as const;
export enum EVariableType {
  Submodel = 'Submodel',
  Date = 'Date',
  Number = 'Number',
  String = 'String',
  Multiselect = 'Multiselect',
  Singleselect = 'Singleselect',
  Boolean = 'Boolean',
  Product = 'PM',
}

export enum EBrowserType {
  Chrome = 'Chrome',
  Edge = 'Edge',
  Safari = 'Safari',
}

export enum EUrlParams {
  ConfigurationId = 'cid',
  Scope = 'cc',
  Token = 'at',
  Language = 'l',
  Country = 'c',
  Currency = 'cur',
  HeaderLess = 'hl',
  ViewId = 'viewId',
  Model = 'model',
  Page = 'Page',
  Limit = 'Limit',
  ProductSearch = 'search',
}

export enum ESalesTextType {
  Long = 'long',
  Short = 'short',
}

export const ApiUrl = {
  SalesTextApi: '/product/v1/salestext/read',
  ImagesApi: '/product/v1/images/read',
} as const;

export enum EActionType {
  Notify = 'notify',
  Reset = 'reset',
  ClearAll = 'clearAll',
  CloseWithoutSaving = 'closeWithoutSaving',
  Success = 'success',
  IncompleteConfiguration = 'incompleteConfiguration',
  Confirm = 'confirm',
  Error = 'error',
  Info = 'info',
}

export enum ESessionStore {
  ConfigurationId = 'configurationId',
  ViewId = 'viewId',
  ProductId = 'productId',
  Scope = 'scope',
  Flow = 'flow',
  IsGuardedTab = 'isGuardedTab',
  PriceListMap = 'priceListMap',
  Language = 'language',
  Country = 'country',
  Currency = 'currency',
  Token = 'token',
  HeaderLess = 'headerLess',
  SearchCount = 'searchCount',
  SearchMatches = 'searchMatches',
  Channel = 'channel',
}

export enum ELocalStore {
  IsUnSupportedBrowserAck = 'isUnsBrowAck',
  ShowAutoSelections = 'showAutoSelections',
}

export enum EChangeType {
  Remove = 'Remove',
  Change = 'Change',
  Add = 'Add',
}

export enum ESectionInfoType {
  Invalid = 'invalid',
  Scope = 'scope',
  Model = 'model',
  Section = 'section',
  MultiNumeric = 'multiNumeric',
  General = 'general',
  Guarded = 'guarded',
}

export enum ETabValue {
  Information = 'Information',
  Attention = 'Attention',
  SalesText = 'SalesText',
  OrderingInstructions = 'OrderingInstructions',
  Images = 'Images',
  ProductPortfolio = 'ProductPortfolio',
  InternalProductPortfolio = 'InternalProductPortfolio',
  MyConfiguration = 'MyConfiguration',
}

export enum EDeviceType {
  Mobile = 'mobile',
  Tablet = 'tablet',
}

export enum EErrorCodes {
  Unauthenticated = 401,
  Unauthorized = 403,
}

export enum EErrorTypes {
  NotAuthenticated = 'NotAuthenticated',
  NotAuthorized = 'NotAuthorized',
  Unsupported = 'Unsupported',
  ContactUS = 'ContactUS',
  InvalidConfiguration = 'InvalidConfiguration',
}

export enum EPageNames {
  LandingPage = '/landingPage',
  Notified = '/notified',
  CloseTab = '/closeTab',
  ErrorPage = '/Error',
  Logout = '/logout',
  Configurator = 'Configurator',
}

export enum EEnvironments {
  DEV = 'DEV',
  PROD = 'PROD',
  TEST = 'TEST',
  ACC = 'ACC',
}

export enum EMessagesType {
  Warning = 'Warn',
  Info = 'Info',
}

export enum EFilterRootOption {
  Channel = 'Channels',
  Application = 'Applications',
  Business = 'Business',
  States = 'States',
}

export enum EPropertyType {
  Bool = 'BOOL',
  Enum = 'ENUM',
  String = 'STR',
  Numeric = 'NUM',
}

export enum EVariableAttribute {
  Suppress = 'Suppress',
  Hide = 'Hide',
}

export enum EReportType {
  GCE = 'GCE',
  CU50 = 'CU50',
}

export enum ESheetType {
  Single = 'Single',
  Multiple = 'Multiple',
}

//This mapping used to remove params from the hybris url
export const ParamMappings = [
  { key: ESessionStore.ConfigurationId, urlKey: EUrlParams.ConfigurationId, remove: false },
  { key: ESessionStore.Scope, urlKey: EUrlParams.Scope, remove: true },
  { key: ESessionStore.Token, urlKey: EUrlParams.Token, remove: true },
  { key: ESessionStore.HeaderLess, urlKey: EUrlParams.HeaderLess, remove: true },
  { key: ESessionStore.Language, urlKey: EUrlParams.Language, remove: true },
  { key: ESessionStore.Country, urlKey: EUrlParams.Country, remove: true },
  { key: ESessionStore.Currency, urlKey: EUrlParams.Currency, remove: true },
];

export enum EMessageType {
  Error = 'error',
  Success = 'success',
  Info = 'info',
  Warning = 'warning',
  Default = 'default',
}

export const StateDisplayOrder = {
  [EConfigurationType.Active]: 1,
  [EConfigurationType.ReadOnly]: 2,
  [EConfigurationType.Temp]: 3,
  [EConfigurationType.Frozen]: 4,
  [EConfigurationType.UnAvailable]: 5,
} as const;

export enum EConfigitStateCustomProperty {
  Hide = 'hide',
  Suppressed = 'suppressed',
  IsItemsAvailable = 'isItemsAvailable',
}

export const SUBMODEL_PREFIX = 'I_';

export enum ECatalogType {
  Release = 'R',
  Internal = 'I',
}
